import React, { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { KeyRound, Eye, EyeOff, ShieldAlert } from "lucide-react";

const inputCls = "w-full h-10 border border-[#E6E4DD] rounded-sm px-3 text-sm text-forest focus:outline-none focus:border-forest";

const PwInput = ({ value, onChange, placeholder, testId }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testId} type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoComplete="new-password" className={`${inputCls} pr-9`} />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-2.5 text-forest/50 hover:text-forest">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
    </div>
  );
};

export const ChangePasswordForm = () => {
  const [f, setF] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const ok = f.current && f.next.length >= 8 && f.next === f.confirm;
  const save = async () => {
    setBusy(true);
    try { await api.post("/auth/change-password", { current_password: f.current, new_password: f.next }); toast.success("Password changed — other devices were signed out"); setF({ current: "", next: "", confirm: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 border-t border-[#E6E4DD] pt-4" data-testid="change-password-form">
      <div className="label-caps flex items-center gap-1.5"><KeyRound className="h-3 w-3" /> Change password</div>
      <PwInput testId="pw-current" value={f.current} onChange={(v) => setF({ ...f, current: v })} placeholder="Current password" />
      <PwInput testId="pw-new" value={f.next} onChange={(v) => setF({ ...f, next: v })} placeholder="New password (min 8 characters)" />
      <PwInput testId="pw-confirm" value={f.confirm} onChange={(v) => setF({ ...f, confirm: v })} placeholder="Confirm new password" />
      {f.confirm && f.next !== f.confirm && <div className="text-xs text-clay">Passwords don't match</div>}
      <button data-testid="pw-save" disabled={!ok || busy} onClick={save} className="h-9 px-4 rounded-sm border border-forest text-forest text-sm hover:bg-forest hover:text-white disabled:opacity-50 transition-colors duration-150">{busy ? "Saving…" : "Update password"}</button>
    </div>
  );
};

export const ResetPasswordDialog = ({ target, endpoint, onClose }) => {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const save = async () => {
    setBusy(true);
    try { await api.post(endpoint, { new_password: pw }); toast.success(`Password reset for ${target.name} — share it with them securely`); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-forest">Reset password</DialogTitle>
          <DialogDescription>Set a new password for <b>{target.name}</b> ({target.email}). They'll be signed out everywhere. Existing passwords are encrypted and can't be viewed.</DialogDescription>
        </DialogHeader>
        <PwInput testId="reset-pw-input" value={pw} onChange={setPw} placeholder="New password (min 8 characters)" />
        <button data-testid="reset-pw-save" disabled={pw.length < 8 || busy} onClick={save} className="h-10 rounded-sm bg-forest text-white text-sm font-medium disabled:opacity-50">{busy ? "Saving…" : "Reset password"}</button>
      </DialogContent>
    </Dialog>
  );
};

export const ImpersonationBanner = () => {
  const { user } = useAuth();
  if (!user?.impersonated_by) return null;
  const back = async () => {
    try { await api.post("/auth/stop-impersonation"); window.location.assign("/super-admin"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="impersonation-banner" className="sticky top-0 z-40 bg-clay text-white px-4 py-2 flex items-center gap-3 text-sm">
      <ShieldAlert className="h-4 w-4 shrink-0" />
      <span className="flex-1">Viewing as <b>{user.name}</b> ({user.role}) · nothing you do here is tracked</span>
      <button data-testid="impersonation-back" onClick={back} className="h-8 px-3 rounded-sm bg-white text-clay text-xs font-bold hover:bg-bone transition-colors duration-150">Back to Super Admin</button>
    </div>
  );
};
